import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import {
  useGetProfile,
  useChangePassword,
  useUploadProfilePic,
  useDeleteProfilePic,
} from "../../../api/addEmployee/employee";
import { notifyError } from "../../../utils/ToastifyProvider";

const Settings = () => {
  const { id } = useParams();
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const { data: profileData, isLoading: isProfileLoading } = useGetProfile();
  const { mutate: changePassword, isLoading: isChanging } = useChangePassword();
  const { mutate: uploadProfilePic, isLoading: isUploading } =
    useUploadProfilePic();
  const { mutate: deleteProfilePic, isLoading: isDeleting } =
    useDeleteProfilePic();

  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmNewPassword: "",
  });

  const BASE_API_URL = process.env.REACT_APP_API_BASE_URL_DOWNLOAD;

  const [profilePic, setProfilePic] = useState(null);

  const profile = useMemo(() => profileData?.user || {}, [profileData]);

  useEffect(() => {
    if (profile?.profilePic) {
      setProfilePic(`${BASE_API_URL}${profile.profilePic}`);
    }
  }, [profile]);

  const handlePasswordModalChange = (field, value) => {
    setPasswordData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      notifyError("New passwords do not match");
      return;
    }

    changePassword(
      {
        oldPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      },
      {
        onSuccess: () => {
          setPasswordData({
            currentPassword: "",
            newPassword: "",
            confirmNewPassword: "",
          });
          setIsPasswordModalOpen(false);
        },
      }
    );
  };

  const handleProfilePicChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePic(URL.createObjectURL(file));
      uploadProfilePic({ id: profile?._id, file });
    }
  };

  const handleDeleteProfilePic = () => {
    if (!profile?._id) return;
    deleteProfilePic(
      { id: profile._id, token: localStorage.getItem("token") },
      { onSuccess: () => setProfilePic(null) }
    );
  };

  const getInitials = (firstName, lastName) => {
    if (!firstName && !lastName) return "NA";
    return `${firstName?.charAt(0) || ""}${
      lastName?.charAt(0) || ""
    }`.toUpperCase();
  };

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg font-medium">Loading profile...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <p className="text-lg font-medium text-red-500">
          Failed to load profile data.
        </p>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-3xl bg-white border border-gray-300 rounded-lg shadow p-8 max-h-screen overflow-y-auto">
        <h2 className="text-3xl font-bold text-center mb-6">
          Profile Settings
        </h2>

        {/* Profile Picture Section */}
        <div className="flex flex-col items-center mb-8">
          <div className="relative w-32 h-32">
            {profilePic ? (
              <img
                src={profilePic}
                alt="Profile"
                className="w-32 h-32 rounded-full object-cover border-4 border-primary"
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-blue-400 flex items-center justify-center text-white text-3xl font-bold border-4">
                {getInitials(profile.firstName, profile.lastName)}
              </div>
            )}

            {/* Upload button */}
            {!profilePic && (
              <label className="absolute bottom-0 right-0 bg-blue-400 text-white rounded-full p-2 cursor-pointer">
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleProfilePicChange}
                />
                {isUploading ? "⏳" : "✎"}
              </label>
            )}
          </div>

          {profilePic && (
            <button
              onClick={() => setIsConfirmModalOpen(true)}
              className="mt-3 text-red-600 text-sm hover:underline disabled:opacity-50"
              disabled={isDeleting}
            >
              {isDeleting ? "Removing..." : "Remove Profile Picture"}
            </button>
          )}

          {isConfirmModalOpen && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
              <div className="bg-white rounded-lg shadow-lg w-full max-w-sm p-6">
                <h3 className="text-lg font-bold mb-4 text-center">
                  Confirm Delete
                </h3>
                <p className="text-center mb-6">
                  Are you sure you want to delete your profile picture?
                </p>
                <div className="flex justify-center gap-4">
                  <button
                    onClick={() => setIsConfirmModalOpen(false)}
                    className="px-4 py-2 border rounded-md hover:bg-gray-100"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      handleDeleteProfilePic();
                      setIsConfirmModalOpen(false);
                    }}
                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:opacity-90"
                  >
                    OK
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* <p className="mt-2 text-sm text-gray-600">
            Click icon to change or remove profile picture
          </p> */}
        </div>

        {/* Readonly fields like EditUser */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <ReadonlyInput label="Username" value={profile.username} />
          <ReadonlyInput label="First Name" value={profile.firstName} />
          <ReadonlyInput label="Last Name" value={profile.lastName} />
          <ReadonlyInput label="Email" value={profile.email} />
          <ReadonlyInput label="Mobile" value={profile.mobile} />
          <ReadonlyInput label="Department" value={profile.department} />
          <ReadonlyInput label="Position" value={profile.position} />
          <ReadonlyInput label="Gender" value={profile.gender} />
        </div>

        <div className="flex justify-end my-8">
          <button
            type="button"
            onClick={() => setIsPasswordModalOpen(true)}
            className="text-primary underline font-medium"
          >
            Change Password
          </button>
        </div>
      </div>

      {/* Change Password Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50 pb-12">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
            <h3 className="text-xl font-bold mb-4 text-center">
              Change Password
            </h3>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <input
                type="password"
                placeholder="Current Password"
                value={passwordData.currentPassword}
                onChange={(e) =>
                  handlePasswordModalChange("currentPassword", e.target.value)
                }
                required
                className="w-full border border-gray-300 rounded-md py-2 px-4"
              />
              <input
                type="password"
                placeholder="New Password"
                value={passwordData.newPassword}
                onChange={(e) =>
                  handlePasswordModalChange("newPassword", e.target.value)
                }
                required
                className="w-full border border-gray-300 rounded-md py-2 px-4"
              />
              <input
                type="password"
                placeholder="Confirm New Password"
                value={passwordData.confirmNewPassword}
                onChange={(e) =>
                  handlePasswordModalChange(
                    "confirmNewPassword",
                    e.target.value
                  )
                }
                required
                className="w-full border border-gray-300 rounded-md py-2 px-4"
              />
              <div className="flex justify-end gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsPasswordModalOpen(false)}
                  className="px-4 py-2 border rounded-md hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={`bg-primary text-white px-6 py-2 rounded-md hover:opacity-90 transition ${
                    isChanging ? "opacity-60 cursor-not-allowed" : ""
                  }`}
                  disabled={isChanging}
                >
                  {isChanging ? "Changing..." : "Change"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const ReadonlyInput = ({ label, value }) => (
  <div>
    <label className="block text-sm font-semibold mb-1">{label}</label>
    <input
      type="text"
      value={value || ""}
      readOnly
      className="w-full border border-gray-300 rounded-md py-2 px-4 bg-gray-100 cursor-not-allowed"
    />
  </div>
);

export default Settings;
